import { createMemo, createSignal, onMount, For, Index, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import { VILLAGER_CAREER_KEYS } from '../../services/villagerCareers';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import Modal from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { EmptyState } from '../../components/EmptyState';
import { Badge } from '../../components/Badge';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import StorageSelect from '../../components/StorageSelect';
import QuantityInput from '../../components/QuantityInput';
import VillagerOfferPicker, { VillagerOfferIcons, useVillagerOfferText, type VillagerOfferChoice } from '../../components/VillagerOfferPicker';
import LocationPicker from '../../components/LocationPicker';
import AreaInput from '../../components/AreaInput';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import { formatDateTime } from '../../services/dateFormat';
import { firstError } from '../../api/envelope';
import type {
    KnownVillagerDto, VillagerHallDto, VillagerOfferDto, VillagerTradeConfigDto, VillagerTradeConfigInputDto,
} from '../../api/generated/schemas';
import styles from './CropFarmer.module.css';
import villagerStyles from './VillagerTrading.module.css';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

const PROFESSION_KEYS = [
    'VILLAGER_TRADING_PROFESSION_FARMER',
    'VILLAGER_TRADING_PROFESSION_LIBRARIAN',
    'VILLAGER_TRADING_PROFESSION_PRIEST',
    'VILLAGER_TRADING_PROFESSION_BLACKSMITH',
    'VILLAGER_TRADING_PROFESSION_BUTCHER',
];

const LINE_OUTCOME_KEYS = [
    'VILLAGER_TRADING_OUTCOME_PENDING',
    'VILLAGER_TRADING_OUTCOME_COMPLETED',
    'VILLAGER_TRADING_OUTCOME_OUT_OF_INPUTS',
    'VILLAGER_TRADING_OUTCOME_NO_VILLAGER',
    'VILLAGER_TRADING_OUTCOME_LOCKED',
    'VILLAGER_TRADING_OUTCOME_FAILED',
    'VILLAGER_TRADING_OUTCOME_CANCELLED',
];

interface HallForm {
    name: string; citybuild: number; location: string;
    x1: number; y1: number; z1: number; x2: number; y2: number; z2: number;
}

type TradeForm = VillagerTradeConfigInputDto & { id: string | null };

const emptyHallForm = (): HallForm => ({ name: '', citybuild: 0, location: '', x1: 0, y1: 0, z1: 0, x2: 0, y2: 0, z2: 0 });

const priceOf = (offer: VillagerOfferDto) => offer.buyFirst.count + (offer.buySecond?.count ?? 0);

export default function VillagerTradingPage() {
    return <BotScoped groupAware><VillagerTradingInner /></BotScoped>;
}

function VillagerTradingInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const offerText = useVillagerOfferText();
    const inv = useInventoryApi();
    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';

    const [halls, setHalls] = createSignal<VillagerHallDto[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const [showHallForm, setShowHallForm] = createSignal(false);
    const [editHallId, setEditHallId] = createSignal<string | null>(null);
    const [hallForm, setHallForm] = createSignal<HallForm>(emptyHallForm());
    const [tradeForm, setTradeForm] = createSignal<TradeForm | null>(null);

    const [villagersByHall, setVillagersByHall] = createSignal<Record<string, KnownVillagerDto[]>>({});
    const [villagersLoading, setVillagersLoading] = createSignal<string | null>(null);
    const [expandedHallId, setExpandedHallId] = createSignal<string | null>(null);

    onMount(async () => {
        try {
            const [hallRes, storageList] = await Promise.all([
                api.villagerTrading.getVillagerHalls(),
                inv.fetchStorages(),
            ]);
            if (isOk(hallRes.data)) setHalls(hallRes.data.value.halls ?? []);
            else toast?.error(firstError(hallRes.data) ?? t('VILLAGER_TRADING_LOAD_FAILED'));
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('VILLAGER_TRADING_LOAD_FAILED'));
        } finally { setLoading(false); }
    });

    async function loadHalls() {
        const res = await api.villagerTrading.getVillagerHalls();
        if (isOk(res.data)) setHalls(res.data.value.halls ?? []);
    }

    async function loadVillagers(hallId: string): Promise<KnownVillagerDto[]> {
        setVillagersLoading(hallId);
        try {
            const res = await api.villagerTrading.getHallVillagers(hallId);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('VILLAGER_TRADING_LOAD_FAILED'));
                return [];
            }
            const villagers = res.data.value.villagers ?? [];
            setVillagersByHall({ ...villagersByHall(), [hallId]: villagers });
            return villagers;
        } finally { setVillagersLoading(null); }
    }

    async function toggleVillagers(hall: VillagerHallDto) {
        if (expandedHallId() === hall.id) { setExpandedHallId(null); return; }
        setExpandedHallId(hall.id);
        await loadVillagers(hall.id);
    }

    const patchHall = (patch: Partial<HallForm>) => setHallForm({ ...hallForm(), ...patch });
    const patchTrade = (patch: Partial<TradeForm>) => setTradeForm({ ...tradeForm()!, ...patch });

    function openCreateHall() {
        setEditHallId(null);
        setHallForm(emptyHallForm());
        setShowHallForm(true);
    }

    function openEditHall(hall: VillagerHallDto) {
        setEditHallId(hall.id);
        setHallForm({
            name: hall.name, citybuild: hall.citybuild, location: hall.location,
            x1: hall.x1, y1: hall.y1, z1: hall.z1, x2: hall.x2, y2: hall.y2, z2: hall.z2,
        });
        setShowHallForm(true);
    }

    async function saveHall(e: Event) {
        e.preventDefault();
        const id = editHallId();
        const form = hallForm();
        const existing = halls().find(h => h.id === id);
        const placeChanged = existing && (
            existing.citybuild !== form.citybuild
            || existing.x1 !== form.x1 || existing.y1 !== form.y1 || existing.z1 !== form.z1
            || existing.x2 !== form.x2 || existing.y2 !== form.y2 || existing.z2 !== form.z2);
        if (placeChanged && !(await showConfirm(t('VILLAGER_TRADING_BOX_CHANGED_CONFIRM'), true))) return;

        setSaving(true);
        try {
            const body = { ...form, name: form.name.trim() || t('VILLAGER_TRADING_DEFAULT_HALL_NAME'), location: form.location.trim() };
            const res = id
                ? await api.villagerTrading.updateVillagerHall(id, body)
                : await api.villagerTrading.createVillagerHall(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('VILLAGER_TRADING_SAVE_FAILED'));
                return;
            }
            setShowHallForm(false);
            toast?.info(id ? t('VILLAGER_TRADING_HALL_UPDATED') : t('VILLAGER_TRADING_HALL_CREATED'));
            if (id && placeChanged) setVillagersByHall({ ...villagersByHall(), [id]: [] });
            await loadHalls();
        } finally { setSaving(false); }
    }

    async function deleteHall(hall: VillagerHallDto) {
        if (!(await showConfirm(t('VILLAGER_TRADING_HALL_DELETE_CONFIRM', hall.name), true))) return;
        const res = await api.villagerTrading.deleteVillagerHall(hall.id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('VILLAGER_TRADING_DELETE_FAILED'));
            return;
        }
        toast?.info(t('VILLAGER_TRADING_HALL_DELETED'));
        await loadHalls();
    }

    async function scanHall(hall: VillagerHallDto) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('VILLAGER_TRADING_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.villagerTrading.scanVillagerHall(hall.id, { botId: targetBotId }),
            { success: t('VILLAGER_TRADING_SCAN_STARTED'), failure: t('VILLAGER_TRADING_START_FAILED') });
    }

    async function openTrade(hall: VillagerHallDto, config?: VillagerTradeConfigDto) {
        if (!villagersByHall()[hall.id]) await loadVillagers(hall.id);
        setTradeForm(config
            ? {
                id: config.id, hallId: config.hallId, name: config.name,
                sourceStorageId: config.sourceStorageId, targetStorageId: config.targetStorageId,
                lines: config.lines.map(line => ({ ...line })), fillerOfferKey: config.fillerOfferKey ?? null,
                maxUnlockAttempts: config.maxUnlockAttempts, minFreeSlots: config.minFreeSlots, isEnabled: config.isEnabled,
            }
            : {
                id: null, hallId: hall.id, name: '',
                sourceStorageId: storages()[0]?.id ?? '', targetStorageId: storages()[0]?.id ?? '',
                lines: [], fillerOfferKey: null, maxUnlockAttempts: 5, minFreeSlots: 4, isEnabled: true,
            });
    }

    async function saveTrade(e: Event) {
        e.preventDefault();
        const form = tradeForm();
        if (!form) return;
        if (!form.sourceStorageId || !form.targetStorageId) { toast?.error(t('VILLAGER_TRADING_SELECT_STORAGES')); return; }
        if (form.lines.length === 0) { toast?.error(t('VILLAGER_TRADING_NO_LINES')); return; }
        setSaving(true);
        try {
            const { id, ...rest } = form;
            const body = { ...rest, name: rest.name.trim() || t('VILLAGER_TRADING_DEFAULT_TRADE_NAME') };
            const res = id
                ? await api.villagerTrading.updateVillagerTradeConfig(id, body)
                : await api.villagerTrading.createVillagerTradeConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('VILLAGER_TRADING_SAVE_FAILED'));
                return;
            }
            setTradeForm(null);
            toast?.info(t('VILLAGER_TRADING_CONFIG_SAVED'));
            await loadHalls();
        } finally { setSaving(false); }
    }

    async function deleteTrade(config: VillagerTradeConfigDto) {
        if (!(await showConfirm(t('VILLAGER_TRADING_CONFIG_DELETE_CONFIRM', config.name), true))) return;
        const res = await api.villagerTrading.deleteVillagerTradeConfig(config.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('VILLAGER_TRADING_DELETE_FAILED')); return; }
        toast?.info(t('VILLAGER_TRADING_CONFIG_DELETED'));
        await loadHalls();
    }

    async function runTrade(config: VillagerTradeConfigDto) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('VILLAGER_TRADING_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.villagerTrading.runVillagerTradeConfig(config.id, { botId: targetBotId }),
            { success: t('VILLAGER_TRADING_TRADE_STARTED'), failure: t('VILLAGER_TRADING_START_FAILED') });
    }

    async function stop() {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('VILLAGER_TRADING_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.villagerTrading.stopVillagerTrading({ botId: targetBotId }),
            { success: t('VILLAGER_TRADING_STOPPED'), failure: t('VILLAGER_TRADING_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? t('VILLAGER_TRADING_UNKNOWN_STORAGE');
    }

    function villagerName(villager: KnownVillagerDto) {
        if (villager.customName) return villager.customName;
        const careerKey = villager.career ? VILLAGER_CAREER_KEYS[villager.career] : undefined;
        return t(careerKey ?? PROFESSION_KEYS[villager.profession] ?? 'VILLAGER_TRADING_PROFESSION_UNKNOWN');
    }

    const offerChoices = createMemo<VillagerOfferChoice[]>(() => {
        const form = tradeForm();
        if (!form) return [];
        const choicesByKey = new Map<string, VillagerOfferChoice>();
        for (const villager of villagersByHall()[form.hallId] ?? []) {
            if (villager.isMissing) continue;
            for (const offer of villager.offers) {
                const price = priceOf(offer);
                const known = choicesByKey.get(offer.offerKey);
                if (!known) { choicesByKey.set(offer.offerKey, { offerKey: offer.offerKey, offer, minPrice: price, maxPrice: price, villagerCount: 1 }); continue; }
                known.villagerCount++;
                if (price < known.minPrice) { known.minPrice = price; known.offer = offer; }
                known.maxPrice = Math.max(known.maxPrice, price);
            }
        }
        return [...choicesByKey.values()];
    });

    const choiceFor = (offerKey: string) => offerChoices().find(choice => choice.offerKey === offerKey);

    function lineOffer(offerKey: string, hallId: string) {
        return choiceFor(offerKey)?.offer
            ?? (villagersByHall()[hallId] ?? []).flatMap(villager => villager.offers).find(candidate => candidate.offerKey === offerKey);
    }

    function setLineAmount(lineIndex: number, amount: number) {
        const form = tradeForm()!;
        patchTrade({ lines: form.lines.map((line, index) => index === lineIndex ? { ...line, amount } : line) });
    }

    function addLine(offerKey: string | null) {
        const form = tradeForm()!;
        const choice = offerKey ? choiceFor(offerKey) : undefined;
        if (!choice || form.lines.some(line => line.offerKey === choice.offerKey)) return;
        patchTrade({ lines: [...form.lines, { offerKey: choice.offerKey, amount: offerText.stackSizeOf(choice.offer.sell) }] });
    }

    const unusedOfferChoices = () => offerChoices().filter(choice => !tradeForm()?.lines.some(line => line.offerKey === choice.offerKey));

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="VILLAGER_TRADING_TITLE"/></h1>
                <div class={styles.headerActions}>
                    <button class="btn btn-secondary" onClick={stop} disabled={!botId()} title={t('VILLAGER_TRADING_STOP_HINT')}>
                        <i class="fa-solid fa-stop" /> <Text val="STOP"/>
                    </button>
                    <button class="btn btn-primary" onClick={openCreateHall}>
                        <i class="fa-solid fa-plus" /> <Text val="VILLAGER_TRADING_NEW_HALL"/>
                    </button>
                </div>
            </div>
            <p class={styles.subtitle}><Text val="VILLAGER_TRADING_SUBTITLE"/></p>

            <Show when={showHallForm()}>
                <form class={`fcard ${styles.form}`} onSubmit={saveHall}>
                    <h2 class={styles.formTitle}>
                        {editHallId() ? <Text val="VILLAGER_TRADING_EDIT_HALL"/> : <Text val="VILLAGER_TRADING_CREATE_HALL"/>}
                    </h2>
                    <div class={styles.formGrid}>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_NAME"/></span>
                            <input class={styles.input} value={hallForm().name} onInput={e => patchHall({ name: e.currentTarget.value })} placeholder={t('VILLAGER_TRADING_HALL_NAME_PLACEHOLDER')} />
                        </label>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_CITYBUILD"/></span>
                            <select class={styles.input} onChange={e => patchHall({ citybuild: parseInt(e.currentTarget.value) })}>
                                <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]} selected={Citybuild[cb] === hallForm().citybuild}>{cb}</option>}</For>
                            </select>
                        </label>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_LOCATION"/></span>
                            <LocationPicker value={hallForm().location} onChange={location => patchHall({ location })} optional />
                        </label>
                    </div>

                    <AreaInput
                        corner1={{ x: hallForm().x1, y: hallForm().y1, z: hallForm().z1 }}
                        corner2={{ x: hallForm().x2, y: hallForm().y2, z: hallForm().z2 }}
                        onChange={(corner1, corner2) => patchHall({
                            x1: corner1.x, y1: corner1.y, z1: corner1.z,
                            x2: corner2.x, y2: corner2.y, z2: corner2.z,
                        })}
                        fieldId="villagerTrading.hall.area"
                        label={t('VILLAGER_TRADING_F_AREA')}
                        citybuild={hallForm().citybuild}
                        allowedBots={botId() ? [botId()] : []} />
                    <span class={styles.fieldHint}><Text val="VILLAGER_TRADING_F_AREA_HINT"/></span>

                    <div class={styles.formActions}>
                        <button type="button" class="btn btn-secondary" onClick={() => setShowHallForm(false)} disabled={saving()}>
                            <Text val="CANCEL"/>
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={saving()}>
                            <i class={saving() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'} /> <Text val="SAVE"/>
                        </button>
                    </div>
                </form>
            </Show>

            <Show when={!loading()} fallback={
                <div class={styles.list}>
                    <Skeleton variant="card" height="14rem" />
                    <Skeleton variant="card" height="14rem" />
                </div>
            }>
                <Show when={halls().length > 0 || showHallForm()} fallback={
                    <EmptyState icon="fa-store" title={t('VILLAGER_TRADING_EMPTY')} hint={t('VILLAGER_TRADING_EMPTY_HINT')}
                        action={<button class="btn btn-primary" onClick={openCreateHall}><i class="fa-solid fa-plus" /> <Text val="VILLAGER_TRADING_NEW_HALL"/></button>} />
                }>
                    <div class={styles.list}>
                        <For each={halls()}>
                            {(hall) => (
                                <div class={`fcard ${styles.card}`}>
                                    <div class={styles.cardHeader}>
                                        <div>
                                            <div class={styles.cardName}>{hall.name}</div>
                                            <div class={styles.cardMeta}>
                                                {hall.citybuildName}{hall.location ? ` · ${hall.location}` : ''}
                                                {' · '}({hall.x1}, {hall.y1}, {hall.z1}) <Text val="VILLAGER_TRADING_TO"/> ({hall.x2}, {hall.y2}, {hall.z2})
                                            </div>
                                        </div>
                                        <div class={styles.cardActions}>
                                            <button class="btn btn-secondary btn-sm" onClick={() => scanHall(hall)} disabled={!botId()} title={t('VILLAGER_TRADING_SCAN_HINT')}>
                                                <i class="fa-solid fa-satellite-dish" /> <Text val="VILLAGER_TRADING_SCAN"/>
                                            </button>
                                            <button class="btn btn-secondary btn-sm btn-icon" onClick={() => openEditHall(hall)} title={t('EDIT')}>
                                                <i class="fa-solid fa-pen" />
                                            </button>
                                            <button class="btn btn-danger btn-sm btn-icon" onClick={() => deleteHall(hall)} title={t('DELETE')}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>

                                    <Show when={hall.lastScannedAt} fallback={
                                        <div class={styles.notScanned}>
                                            <i class="fa-solid fa-circle-info" /> <Text val="VILLAGER_TRADING_NOT_SCANNED"/>
                                        </div>
                                    }>
                                        <div class={styles.stats}>
                                            <div class={styles.stat}><span class={styles.statValue}>{hall.villagerCount}</span><span class={styles.statLabel}><Text val="VILLAGER_TRADING_STAT_VILLAGERS"/></span></div>
                                            <div class={styles.stat}><span class={styles.statValue}>{hall.offerCount}</span><span class={styles.statLabel}><Text val="VILLAGER_TRADING_STAT_OFFERS"/></span></div>
                                        </div>
                                        <div class={styles.scannedAt}>
                                            {t('VILLAGER_TRADING_LAST_SCAN', formatDateTime(hall.lastScannedAt))}
                                        </div>

                                        <div class={styles.section}>
                                            <div class={styles.sectionHeader}>
                                                <span class={styles.sectionTitle}><i class="fa-solid fa-users" /> <Text val="VILLAGER_TRADING_VILLAGERS"/></span>
                                                <button class="btn btn-secondary btn-sm" onClick={() => toggleVillagers(hall)}>
                                                    <i class={expandedHallId() === hall.id ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'} />
                                                    {' '}{expandedHallId() === hall.id ? <Text val="VILLAGER_TRADING_HIDE_VILLAGERS"/> : <Text val="VILLAGER_TRADING_SHOW_VILLAGERS"/>}
                                                </button>
                                            </div>
                                            <Show when={expandedHallId() === hall.id}>
                                                <Show when={villagersLoading() !== hall.id} fallback={
                                                    <div class={villagerStyles.inlineWait}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>
                                                }>
                                                    <Show when={(villagersByHall()[hall.id] ?? []).length > 0} fallback={<p class={styles.sectionEmpty}><Text val="VILLAGER_TRADING_NO_VILLAGERS"/></p>}>
                                                        <div class={villagerStyles.villagerList}>
                                                            <For each={villagersByHall()[hall.id]}>
                                                                {(villager) => (
                                                                    <div class={`${villagerStyles.villager} ${villager.isMissing ? styles.configDisabled : ''}`}>
                                                                        <div class={villagerStyles.villagerHeader}>
                                                                            <span class={styles.configName}>{villagerName(villager)}</span>
                                                                            <span class={styles.configMeta}>{villager.x} {villager.y} {villager.z}</span>
                                                                            <Show when={villager.isMissing}>
                                                                                <Badge tone="warning" icon="fa-circle-question"><Text val="VILLAGER_TRADING_MISSING"/></Badge>
                                                                            </Show>
                                                                        </div>
                                                                        <div class={villagerStyles.offerList}>
                                                                            <For each={villager.offers}>
                                                                                {(offer) => (
                                                                                    <div class={villagerStyles.offerRow}>
                                                                                        <VillagerOfferIcons offer={offer} />
                                                                                        <span class={villagerStyles.uses}>{offer.uses}/{offer.maxUses}</span>
                                                                                        <Show when={offer.isLocked}>
                                                                                            <Badge tone="error" icon="fa-lock"><Text val="VILLAGER_TRADING_LOCKED"/></Badge>
                                                                                        </Show>
                                                                                    </div>
                                                                                )}
                                                                            </For>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </For>
                                                        </div>
                                                    </Show>
                                                </Show>
                                            </Show>
                                        </div>
                                    </Show>

                                    <div class={styles.section}>
                                        <div class={styles.sectionHeader}>
                                            <span class={styles.sectionTitle}><i class="fa-solid fa-right-left" /> <Text val="VILLAGER_TRADING_TRADES"/></span>
                                            <button class="btn btn-secondary btn-sm" onClick={() => openTrade(hall)} disabled={!hall.lastScannedAt} title={hall.lastScannedAt ? undefined : t('VILLAGER_TRADING_SCAN_FIRST')}>
                                                <i class="fa-solid fa-plus" /> <Text val="VILLAGER_TRADING_ADD_TRADE"/>
                                            </button>
                                        </div>
                                        <Show when={hall.tradeConfigs.length > 0} fallback={<p class={styles.sectionEmpty}><Text val="VILLAGER_TRADING_NO_TRADES"/></p>}>
                                            <For each={hall.tradeConfigs}>
                                                {(config) => (
                                                    <div class={`${styles.configRow} ${!config.isEnabled ? styles.configDisabled : ''}`}>
                                                        <div class={styles.configInfo}>
                                                            <span class={styles.configName}>{config.name}</span>
                                                            <span class={styles.configMeta}>
                                                                <i class="fa-solid fa-warehouse" /> {storageName(config.sourceStorageId)}
                                                                {' '}<i class="fa-solid fa-arrow-right" />{' '}
                                                                <i class="fa-solid fa-warehouse" /> {storageName(config.targetStorageId)}
                                                                {' · '}{t('VILLAGER_TRADING_LINE_COUNT', String(config.lines.length))}
                                                            </span>
                                                            <Show when={config.lastRun}>
                                                                {(lastRun) => (
                                                                    <span class={styles.configMeta}>
                                                                        {t('VILLAGER_TRADING_LAST_RUN', formatDateTime(lastRun().startedAt))}
                                                                        <For each={lastRun().lines}>
                                                                            {(line) => <span class={styles.mixChip}>{line.received}/{line.requested} {t(LINE_OUTCOME_KEYS[line.outcome] ?? 'VILLAGER_TRADING_OUTCOME_PENDING')}</span>}
                                                                        </For>
                                                                        <Show when={lastRun().unlocks > 0}>
                                                                            <span class={styles.mixChip}>{t('VILLAGER_TRADING_UNLOCKS', String(lastRun().unlocks))}</span>
                                                                        </Show>
                                                                        <Show when={lastRun().depositFailed}>
                                                                            <Badge tone="error"><Text val="VILLAGER_TRADING_DEPOSIT_FAILED"/></Badge>
                                                                        </Show>
                                                                    </span>
                                                                )}
                                                            </Show>
                                                        </div>
                                                        <div class={styles.cardActions}>
                                                            <button class="btn btn-primary btn-sm" onClick={() => runTrade(config)} disabled={!botId() || !config.isEnabled}>
                                                                <i class="fa-solid fa-play" /> <Text val="RUN"/>
                                                            </button>
                                                            <button class="btn btn-secondary btn-sm btn-icon" onClick={() => openTrade(hall, config)} title={t('EDIT')}>
                                                                <i class="fa-solid fa-pen" />
                                                            </button>
                                                            <button class="btn btn-danger btn-sm btn-icon" onClick={() => deleteTrade(config)} title={t('DELETE')}>
                                                                <i class="fa-solid fa-trash" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </For>
                                        </Show>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </Show>

            <Modal open={tradeForm() !== null} onClose={() => setTradeForm(null)} busy={saving()}
                title={tradeForm()?.id ? <Text val="VILLAGER_TRADING_EDIT_TRADE"/> : <Text val="VILLAGER_TRADING_ADD_TRADE"/>}>
                <Show when={tradeForm()}>
                    {(form) => (
                        <form class={styles.form} onSubmit={saveTrade}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_NAME"/></span>
                                <input class={styles.input} value={form().name} onInput={e => patchTrade({ name: e.currentTarget.value })} placeholder={t('VILLAGER_TRADING_DEFAULT_TRADE_NAME')} />
                            </label>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_SOURCE_STORAGE"/></span>
                                <StorageSelect storages={storages()} value={form().sourceStorageId} onChange={sourceStorageId => patchTrade({ sourceStorageId })} placeholder={t('VILLAGER_TRADING_SELECT_STORAGE_OPTION')} />
                                <span class={styles.fieldHint}><Text val="VILLAGER_TRADING_F_SOURCE_STORAGE_HINT"/></span>
                            </label>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_TARGET_STORAGE"/></span>
                                <StorageSelect storages={storages()} value={form().targetStorageId} onChange={targetStorageId => patchTrade({ targetStorageId })} placeholder={t('VILLAGER_TRADING_SELECT_STORAGE_OPTION')} />
                                <span class={styles.fieldHint}><Text val="VILLAGER_TRADING_F_TARGET_STORAGE_HINT"/></span>
                            </label>

                            <div class={styles.coordGroup}>
                                <span class={styles.coordGroupLabel}><Text val="VILLAGER_TRADING_F_LINES"/></span>
                                <Show when={form().lines.length > 0} fallback={<p class={styles.sectionEmpty}><Text val="VILLAGER_TRADING_NO_LINES"/></p>}>
                                    <Index each={form().lines}>
                                        {(line, lineIndex) => (
                                            <div class={villagerStyles.lineRow}>
                                                <Show when={lineOffer(line().offerKey, form().hallId)} fallback={
                                                    <span class={`${styles.configMeta} ${villagerStyles.lineOffer}`}><Text val="VILLAGER_TRADING_OFFER_NOT_CACHED"/></span>
                                                }>
                                                    {(offer) => (
                                                        <span class={villagerStyles.lineOffer}>
                                                            <VillagerOfferIcons offer={offer()} />
                                                            <span class={villagerStyles.lineText}>
                                                                <span class={styles.configName}>{offerText.itemLabel(offer().sell)}</span>
                                                                <Show when={choiceFor(line().offerKey)}>
                                                                    {(choice) => <span class={styles.configMeta}>{offerText.priceLabel(choice())}</span>}
                                                                </Show>
                                                            </span>
                                                        </span>
                                                    )}
                                                </Show>
                                                <Show when={lineOffer(line().offerKey, form().hallId)} fallback={<span />}>
                                                    {(offer) => (
                                                        <div class={villagerStyles.amountField}>
                                                            <QuantityInput inline min={1} value={line().amount} stackSize={offerText.stackSizeOf(offer().sell)}
                                                                onChange={amount => setLineAmount(lineIndex, amount)}
                                                                placeholder={t('VILLAGER_TRADING_F_AMOUNT')} />
                                                            <span class={styles.configMeta}>
                                                                {t('VILLAGER_TRADING_LINE_TRADES', String(Math.ceil(line().amount / Math.max(1, offer().sell.count))))}
                                                            </span>
                                                        </div>
                                                    )}
                                                </Show>
                                                <button type="button" class={`btn btn-danger btn-sm btn-icon ${villagerStyles.lineDelete}`} title={t('DELETE')}
                                                    onClick={() => patchTrade({ lines: form().lines.filter((_, index) => index !== lineIndex) })}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        )}
                                    </Index>
                                </Show>
                                <VillagerOfferPicker choices={unusedOfferChoices()} value={null} onChange={addLine}
                                    placeholder={t(unusedOfferChoices().length > 0 ? 'VILLAGER_TRADING_ADD_LINE_PLACEHOLDER' : 'VILLAGER_TRADING_NO_MORE_OFFERS')} />
                            </div>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_FILLER"/></span>
                                <VillagerOfferPicker choices={offerChoices()} value={form().fillerOfferKey ?? null}
                                    onChange={fillerOfferKey => patchTrade({ fillerOfferKey })}
                                    placeholder={t('VILLAGER_TRADING_NO_FILLER')}
                                    emptyOption={{ label: t('VILLAGER_TRADING_NO_FILLER'), icon: 'fa-forward' }} />
                                <span class={styles.fieldHint}><Text val="VILLAGER_TRADING_F_FILLER_HINT"/></span>
                            </label>
                            <Show when={form().fillerOfferKey}>
                                <label class={styles.field}>
                                    <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_UNLOCK_ATTEMPTS"/></span>
                                    <input class={styles.input} type="number" min="0" max="20" value={form().maxUnlockAttempts}
                                        onInput={e => patchTrade({ maxUnlockAttempts: Math.max(0, Math.min(20, parseInt(e.currentTarget.value) || 0)) })} />
                                </label>
                            </Show>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="VILLAGER_TRADING_F_MINFREE"/></span>
                                <input class={styles.input} type="number" min="1" max="35" value={form().minFreeSlots} onInput={e => patchTrade({ minFreeSlots: parseInt(e.currentTarget.value) || 4 })} />
                            </label>
                            <label class={styles.checkField}>
                                <input type="checkbox" checked={form().isEnabled} onChange={e => patchTrade({ isEnabled: e.currentTarget.checked })} />
                                <span><Text val="VILLAGER_TRADING_F_ENABLED"/></span>
                            </label>
                            <div class={styles.formActions}>
                                <button type="button" class="btn btn-secondary" onClick={() => setTradeForm(null)} disabled={saving()}><Text val="CANCEL"/></button>
                                <button type="submit" class="btn btn-primary" disabled={saving() || !form().sourceStorageId || !form().targetStorageId || form().lines.length === 0}>
                                    <i class={saving() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'} /> <Text val="SAVE"/>
                                </button>
                            </div>
                        </form>
                    )}
                </Show>
            </Modal>
        </div>
    );
}
